/* eslint-disable react/jsx-boolean-value */
import * as uuid from "uuid";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import AdGroupsDashboard from "../../../../../views/CampaignCreationBasicSlides/AdGroupSelectionSlide/components/AdGroupsDashboard";
import Button from "../../../../../components/elements/Button";
import Loader from "../../../../../components/elements/Loader";
import NoAdGroupsFound from "../../../../../views/CampaignCreationBasicSlides/AdGroupSelectionSlide/components/NoAdgroupsFound";
import api from "../../../../../api";
import classes from "./UploadCreativesModal.module.scss";
import { AdGroup, AdGroupTemplate, AdGroupType, CreativeAsset } from "../../../../../types/adGroupTypes";
import { CreativeIdsToDelete } from "../../../../../types/creativeTypes";
import { ReduxApplicationState } from "../../../../../types/ReduxTypes/ReduxApplicationState";
import {
  createAdGroup,
  getAdGroupTemplates,
  uploadCreativeAssetsAndCreateTemplates,
} from "../../../../../redux/reducers/campaignToCreate";
import { errorToaster, successToaster } from "../../../../../helpers/toasters";

type UploadCreativeModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  beeswaxAdvertiserId: number;
  brandgenAdvertiserId: string;
  landingPage: string;
  fetchedAdGroupId: string;
  fetchAndSetAdGroup: () => Promise<void>;
  brandgenCampaignId: string;
  fetchedCreativesAssets: CreativeAsset[];
};

const getCampaignById = async (brandgenCampaignId: string) => {
  try {
    return await api.getCampaignById(brandgenCampaignId);
  } catch (error) {
    const err = error as any;
    throw new Error(err);
  }
};

const UploadFromTemplateModal: FC<UploadCreativeModalProps> = ({
  isOpen,
  handleClose,
  beeswaxAdvertiserId,
  brandgenAdvertiserId,
  landingPage,
  fetchedAdGroupId,
  fetchAndSetAdGroup,
  brandgenCampaignId,
  fetchedCreativesAssets,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const dispatch = useDispatch();

  const user = useSelector((state: ReduxApplicationState) => state.user);
  const adGroupToCreate = useSelector((state: ReduxApplicationState) => state.campaignToCreate.adGroupToCreate);

  const handleUpload = async () => {
    const { name, description, creativeAssets } = adGroupToCreate;
    try {
      if (creativeAssets.length !== 0 && !isDisabled) {
        setIsLoading(true);
        setIsDisabled(true);
        const adGroupType = creativeAssets[0].fileType as AdGroupType;
        const adGroupToCreateBody = {
          adGroupId: `bg-ad-group-${uuid.v4()}`,
          brandgenAdvertiserId: brandgenAdvertiserId!,
          brandgenCampaignId,
          createdBy: user.currentUser,
          name,
          adGroupType,
          description: description ?? "",
        };
        const createdAdGroup: AdGroup = await createAdGroup(adGroupToCreateBody);
        if (createdAdGroup) {
          await uploadCreativeAssetsAndCreateTemplates(
            creativeAssets,
            beeswaxAdvertiserId.toString(),
            brandgenAdvertiserId,
            landingPage,
            createdAdGroup.adGroupId,
            "",
            false,
            api.changeCreative,
            brandgenCampaignId,
          );

          // Deletion
          await api.deleteAdGroup(fetchedAdGroupId);

          const unresolvedPromises = fetchedCreativesAssets.map((fetchedCreativeAsset) => {
            const creativeIdsToDelete: CreativeIdsToDelete = {
              bgCreativeId: fetchedCreativeAsset.bgCreativeId,
              beeswaxCreativeId: fetchedCreativeAsset.beeswaxCreativeId,
            };
            return api.deleteCreative(creativeIdsToDelete);
          });
          await Promise.all(unresolvedPromises);
        }
        await fetchAndSetAdGroup();
        setIsLoading(false);
        setIsDisabled(false);
        handleClose();
        successToaster("Creatives successfully replaced.", 2000);
      } else {
        errorToaster("Please, select Ad Group to continue.", 5000);
      }
    } catch (error) {
      const err = error as any;
      setIsLoading(false);
      setIsDisabled(false);
      errorToaster(`There was an error: ${err.message}`);
    }
  };

  const selectedAdvertiser = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser)!;
  const adGroupTemplates = useSelector((state: ReduxApplicationState) => state.campaignToCreate.adGroupTemplates);

  const isAdGroupsPending = useSelector((state: ReduxApplicationState) => state.campaignToCreate.isPending);
  const [filteredAdGroupsTemplates, setFilteredAdGroupsTemplates] = useState<AdGroupTemplate[]>([]);

  useEffect(() => {
    dispatch(getAdGroupTemplates(brandgenAdvertiserId));
  }, [adGroupTemplates.length, dispatch, brandgenAdvertiserId]);

  useEffect(() => {
    (async () => {
      const { payload: campaign } = await getCampaignById(brandgenCampaignId);
      setFilteredAdGroupsTemplates(
        adGroupTemplates.filter((adGroupTemplate) =>
          campaign.isVideoCampaign
            ? adGroupTemplate.adGroupType === AdGroupType.VIDEO
            : adGroupTemplate.adGroupType === AdGroupType.IMAGE,
        ),
      );
    })();
  }, [adGroupTemplates, brandgenCampaignId]);

  return (
    <Dialog
      open={isOpen}
      onClose={isLoading ? () => null : handleClose}
      aria-labelledby="Segment Creation Modal"
      className={classes.container}
      maxWidth="lg"
    >
      <div className={classes.dialogTitle}>
        <Typography variant="h2">Choose creatives from templates</Typography>
      </div>
      {isLoading ? (
        <div className={classes.loader}>
          <Loader isAbsolutePosition={false} size={36} />
        </div>
      ) : (
        <DialogContent className={classes.dialogContent} style={{ display: "flex", flexDirection: "column" }}>
          {isAdGroupsPending ? (
            <Typography className={classes.loaderLabel}>Please, wait while we get everything in order...</Typography>
          ) : !isAdGroupsPending && adGroupTemplates.length === 0 ? (
            <NoAdGroupsFound selectedAdvertiser={selectedAdvertiser} />
          ) : (
            <AdGroupsDashboard
              selectedAdvertiser={selectedAdvertiser}
              adGroupTemplates={filteredAdGroupsTemplates}
              showCheckbox={true}
            />
          )}
          <Button
            variant="outlined"
            disabled={isDisabled}
            onClick={handleUpload}
            style={{
              marginTop: 10,
              marginLeft: "auto",
              alignSelf: "flex-end",
              maxWidth: 150,
            }}
          >
            Choose
          </Button>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default UploadFromTemplateModal;
